import type { Configuration, InitParams, LibParams } from '@hotstar/config';
import { ConfigLib } from '@hotstar/config';
import { Platform } from '@hotstar/platforms';
import type { PubSubCallBack } from '@hotstar/utils';
import { captureEvent } from '@hotstar/tracker';
import { recordAppStartBreakupEvent, AppStartBreakupEvents } from '@hotstar/app-start-breakup';
import type { ConfigLibServiceProvider } from './types';
import { getNetworkInstance, handleRequestRetryConfigChange } from '../network-instance';
import { getConsoleDestInstance, ModuleLogger } from '../logger';
import { CLIENT_CONFIG_PREFIX } from '../api-constants';
import { configFilterList, PLATFORMS_WITH_CONFIG } from './common';

const configLogger = new ModuleLogger(
  {
    moduleName: 'Config Client Lib',
    category: 'config-logger-web'
  },
  [getConsoleDestInstance()]
);

const params: LibParams = {
  configEndPoint: CLIENT_CONFIG_PREFIX,
  networkInstance: getNetworkInstance(),
  logger: configLogger,
  fallbackConfig: {}
};

const configInstance = new ConfigLib(params);
let isInitiated = false;

const getInstance = () => {
  if (isInitiated) {
    return configInstance;
  }
  throw new Error('Library not initiated');
};

let activeCountry: string;
let activePlatform: Platform;

const getValue = (key: string) => getInstance().getValue(key, activeCountry, activePlatform);

const getStringValueWithFallback = (key: string, fallbackValue: string) => {
  let valueFromConfig = null;
  try {
    valueFromConfig = getValue(key);
  } catch (error: unknown) {
    captureEvent({
      message: typeof error === 'string' ? error : '',
      extra: {
        description: 'attempted to get config value before lib init, returning fallback value',
        configKey: key,
        fallbackValue,
        source: 'client'
      }
    });
    return fallbackValue;
  }
  return typeof valueFromConfig === 'string' ? valueFromConfig : fallbackValue;
};

const subscribe = (key: string, callback: PubSubCallBack) =>
  getInstance().subscribe(key, callback, activeCountry, activePlatform);

const unSubscribe = (key: string, callback: PubSubCallBack) =>
  getInstance().unsubscribe(key, callback, activeCountry, activePlatform);

const getActiveCountry = () => activeCountry;

const setActiveCountry = (country: string) => {
  activeCountry = country;
};

const getActivePlatform = (): Platform => activePlatform;

const setActivePlatform = (platform: Platform) => {
  // Fall back to Platform.WEBOS if platform doesn't have own configuration
  activePlatform = PLATFORMS_WITH_CONFIG.includes(platform) ? platform : Platform.WEBOS;
};

const prepare = async (country: string, platform: Platform, defaultConfig?: Configuration) => {
  recordAppStartBreakupEvent(AppStartBreakupEvents.configLibInitStart);
  if (isInitiated) {
    return;
  }

  activeCountry = country;

  setActivePlatform(platform);

  const initParams: InitParams = {
    countries: [activeCountry],
    appVersion: process.env.APP_VERSION,
    platforms: [activePlatform],
    filterList: configFilterList,
    enablePolling: defaultConfig
      ? (defaultConfig['web.enable_config_polling'] as boolean) ?? true
      : true
  };

  if (defaultConfig && Object.keys(defaultConfig).length > 0) {
    initParams.defaultConfig = defaultConfig;
  } else {
    initParams.defaultConfig = (await import('./fallback-object.json')).default;
  }

  configInstance.init(initParams).finally(() => {
    recordAppStartBreakupEvent(AppStartBreakupEvents.configLibInitFinished);
  });

  configInstance.subscribe(
    'web.retry_requests_on_server_error',
    handleRequestRetryConfigChange,
    activeCountry,
    activePlatform
  );

  configInstance.subscribe(
    'web.enable_config_polling',
    /* istanbul ignore next */
    (isPollingEnabled) => {
      if (isPollingEnabled) {
        configInstance.startPolling(activeCountry, activePlatform);
      } else {
        configInstance.stopPolling(activeCountry, activePlatform);
      }
    },
    activeCountry,
    activePlatform
  );

  isInitiated = true;
  recordAppStartBreakupEvent(AppStartBreakupEvents.configPrepared);
};

const configLibServiceProvider: ConfigLibServiceProvider = {
  getInstance,
  prepare,
  getValue,
  getStringValueWithFallback,
  getActiveCountry,
  setActiveCountry,
  getActivePlatform,
  setActivePlatform,
  subscribe,
  unSubscribe
};

export { configLibServiceProvider };
